import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, MapPin, Phone } from "lucide-react";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  FEES,
  FIRM,
  MICHAEL_PHONE,
  MICHAEL_PHONE_HREF,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
  faqSchema,
  peninsulaBusinessSchema,
} from "@/lib/firm";

const DESCRIPTION = `Local solicitors for Redcliffe and Scarborough. Coastside Law's office at ${PENINSULA_ADDRESS}. Solicitor-led conveyancing (sales ${FEES.sale}, purchases ${FEES.purchase} incl. GST), wills, estates and elder law for Scarborough, Redcliffe and the wider Moreton Bay region. Call Michael Klein on ${MICHAEL_PHONE}.`;

const FAQS = [
  {
    q: "Where is the Scarborough office?",
    a: `${PENINSULA_ADDRESS}. It is seen by appointment, including evenings and weekends where that suits you better, and Michael Klein will come to you at home or in hospital anywhere on the peninsula.`,
  },
  {
    q: "Do you act for Redcliffe clients?",
    a: `Yes. Our peninsula office is in Scarborough, a few minutes from Redcliffe, and we act for clients in Redcliffe, Clontarf, Margate, Woody Point, Newport and Kippa-Ring on conveyancing, wills, probate, commercial property and business matters.`,
  },
  {
    q: "Do I have to come into the office to do a conveyance?",
    a: "No. Queensland conveyancing settles electronically through PEXA, contracts are signed digitally and identity can be verified by video. The office is there if you would rather sit down with someone.",
  },
  {
    q: "What does a sale or purchase cost?",
    a: `Sales are ${FEES.sale} and purchases ${FEES.purchase}, both inclusive of GST, with pre-signing contract review free of charge. Complex or commercial matters are quoted before any work starts.`,
  },
  {
    q: "Who will I be dealing with?",
    a: `Michael Klein, Legal Practice Director. He was admitted in 2003, lives on the peninsula and spent many years as managing partner of GKS Law. His direct line is ${MICHAEL_PHONE}.`,
  },
];

const SERVICES = [
  {
    to: "/conveyancing" as const,
    title: "Conveyancing",
    body: `Fixed-fee sales (${FEES.sale}) and purchases (${FEES.purchase}) incl. GST, plus free pre-signing contract review.`,
  },
  {
    to: "/scarborough-wills-and-estates" as const,
    title: "Wills & estates — Scarborough",
    body: "Fixed-fee wills (from $660), will + EPOA packs ($825) and couples packs ($1,499), with home and hospital visits.",
  },
  {
    to: "/body-corporate-scarborough" as const,
    title: "Body corporate & units",
    body: "By-laws, levies and recovery, committee decisions, disputes and buying into an esplanade scheme.",
  },
  {
    to: "/elder-law" as const,
    title: "Elder law",
    body: "Downsizing, retirement village and over-50s contracts, granny flat arrangements and capacity issues.",
  },
  {
    to: "/business-sales" as const,
    title: "Business & commercial",
    body: "Business sales and purchases, leases, management rights and commercial agreements.",
  },
  {
    to: "/estate-disputes" as const,
    title: "Estate disputes",
    body: "Family provision claims, will validity and executor disputes, run locally rather than referred to town.",
  },
  {
    to: "/moreton-bay" as const,
    title: "Across Moreton Bay",
    body: "Suburb-by-suburb notes for Redcliffe, Margate, Clontarf, North Lakes, Caboolture and beyond.",
  },
];

const NEARBY = [
  "redcliffe",
  "margate",
  "north-lakes",
  "kallangur",
  "caboolture",
] as const;

export const Route = createFileRoute("/scarborough")({
  head: () => ({
    meta: [
      { title: fitTitle(`Solicitors Redcliffe & Scarborough | ${FIRM.name}`) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(`Solicitors Redcliffe & Scarborough | ${FIRM.name}`) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/scarborough" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/scarborough" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(peninsulaBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: ScarboroughHub,
});

function ScarboroughHub() {
  return (
    <>
      <PageHero
        eyebrow="Redcliffe Peninsula office"
        title="Solicitors for Redcliffe and Scarborough"
        lead={`We are at ${PENINSULA_OFFICE.street}, ${PENINSULA_OFFICE.suburb}, by appointment. Legal Practice Director Michael Klein lives on the peninsula, was managing partner of GKS Law for many years, and acts for Scarborough clients on fixed fees agreed before the work starts.`}
      />

      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-serif text-xl text-foreground">
              <MapPin className="size-5 text-primary" aria-hidden="true" />
              {PENINSULA_OFFICE.label}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {PENINSULA_ADDRESS}
              <br />
              {PENINSULA_OFFICE.note}. Home and hospital visits across the peninsula.
            </p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-serif text-xl text-foreground">
              <Phone className="size-5 text-primary" aria-hidden="true" />
              Direct line
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Michael Klein —{" "}
              <a href={MICHAEL_PHONE_HREF} className="font-semibold text-primary underline">
                {MICHAEL_PHONE}
              </a>
              <br />
              {FIRM.email}
            </p>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">How we help Scarborough clients</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group flex h-full flex-col rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
              >
                <span className="font-serif text-xl text-foreground group-hover:text-primary">
                  {s.title}
                </span>
                <span className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</span>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Read more <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Lawyers for the whole Redcliffe Peninsula</h2>
          <div className="mt-5 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              If you are looking for a solicitor in Redcliffe, our Scarborough office is a few
              minutes up the road. We act for people across the peninsula — Redcliffe, Clontarf,
              Margate, Woody Point, Newport, Kippa-Ring and Rothwell — and you deal directly with a
              solicitor, not a call centre.
            </p>
            <p>
              Most of our peninsula work is residential and{" "}
              <Link to="/commercial-conveyancing" className="font-semibold text-primary underline">commercial conveyancing</Link>,
              wills and powers of attorney, probate and{" "}
              <Link to="/retail-shop-leases" className="font-semibold text-primary underline">retail shop leases</Link>.
              Every fixed fee is confirmed in writing before we start.
            </p>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Local property, honestly assessed</h2>
          <div className="mt-5 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              Scarborough stock runs from long-held waterfront and elevated homes through to
              esplanade unit buildings and over-50s and retirement village interests. Each of those
              behaves differently on a contract, and the search list should change with them — body
              corporate records and sinking fund position on units, flood and erosion overlays near
              the water, and exit fees and reinstatement obligations on village and over-50s
              contracts.
            </p>
            <p>
              A pre-signing contract review is free. If a residential contract ends under a standard
              condition, we do not charge a professional fee for the work already done — you only
              pay third-party disbursements actually incurred.
            </p>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Nearby suburbs</h2>
          <ul className="mt-5 flex flex-wrap gap-3">
            {NEARBY.map((slug) => (
              <li key={slug}>
                <Link
                  to="/moreton-bay/$suburb"
                  params={{ suburb: slug }}
                  className="inline-flex rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
                >
                  {slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/moreton-bay"
                className="inline-flex rounded-sm border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
              >
                All Moreton Bay suburbs
              </Link>
            </li>
          </ul>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Questions people ask</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>

        <div className="mt-14">
          <ReviewStrip />
        </div>

        <div className="mt-14">
          <EnquiryForm heading="Ask Michael about your Scarborough matter" />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
